<script setup>
import { computed, toRefs } from 'vue';
import { useI18n } from 'vue-i18n';
import ImageComponent from '@/components/common/ImageComponent.vue';
import PreviewHeader from '@/components/preview/kakao/common/PreviewHeader.vue';
import PreviewFooter from '@/components/preview/kakao/common/PreviewFooter.vue';
import { COUPON_TYPE_OPTIONS } from '@/config/constant';
import ConsentInformation from './ConsentInformation.vue';

const { t } = useI18n();

const props = defineProps({
  position: {
    type: String,
    default: '',
  },
  brandDefault: {
    type: Object,
    default: () => ({}),
  },
  messageType: {
    type: String,
    default: '',
  },
  kakaoProfile: {
    type: String,
    default: '',
  },
});

const {
  messageType,
  brandDefault,
  position,
} = toRefs(props);

const imageClass = computed(() => {
  const classList = [];

  const map = {
    '': 'd-none',
    BRANDTALK_TEXT: 'd-none',
    BRANDTALK_IMAGE: '',
    BRANDTALK_WIDE_IMAGE: 'wide',
  };

  const hover = position.value === 'default-image';

  if (hover) {
    classList.push('editAreaPositionHover');
  }

  classList.push(map[messageType.value]);

  return classList.join(' ');
});

const couponType = COUPON_TYPE_OPTIONS.reduce((current, item) => ({
  [`${item.type}`]: item.text,
  ...current,
}), {});

const imageUrl = computed(() => brandDefault.value?.image?.url);

const contentList = computed(() => {
  if (messageType.value === '') {
    return '';
  }

  return (brandDefault.value.content || t('kakaoBasic.preview.content'))
    .split('\n')
    .map((v) => v || '\u00A0');
});

const contentClass = computed(() => {
  const classList = [];
  const hover = position.value === 'default-content';

  if (hover) {
    classList.push('editAreaPositionHover');
  }

  return classList.join(' ');
});

const buttonsClass = computed(() => {
  const classList = [];
  const hover = position.value === 'default-buttons';

  if (hover && brandDefault.value?.buttons?.length) {
    classList.push('editAreaPositionHover');
  }

  return classList.join(' ');
});

const couponClass = computed(() => {
  const classList = [];
  const hover = position.value === 'default-coupon';

  if (hover) {
    classList.push('editAreaPositionHover');
  }

  if (!brandDefault.value.coupon?.enabled) {
    classList.push('d-none');
  }

  return classList.join(' ');
});

const couponTitle = computed(() => {
  const title = brandDefault.value.coupon?.name;
  const type = brandDefault.value.coupon?.type;

  const str = t(couponType[type] ?? 'empty');
  const value = type === 'DELIVERY_DISCOUNT' ? str : `${title}${str.slice(2)}`;

  return value;
});

const couponDescription = computed(() => brandDefault.value.coupon?.description || '');

defineOptions({
  inheritAttrs: false,
});

</script>
<template>
  <div class="preview-talk preview-talk--active">
    <ConsentInformation />
    <div class="preview-inner mockup-scroll">
      <PreviewHeader
        :kakao-profile="kakaoProfile"
      />
      <div class="preview-message preview-message--basic preview-message--active">
        <div
          class="preview-message__inner-img"
          :class="imageClass"
        >
          <ImageComponent
            :src="imageUrl"
            alt="default-image"
          />
        </div>
        <div class="preview-message__inner">
          <div
            class="preview-message__inner-cont"
            :class="contentClass"
          >
            <p
              v-for="(elm, index) in contentList"
              :key="`kakao-text-${index}`"
            >
              {{ elm }}
            </p>
          </div>
        </div>
        <div
          class="preview-message__button"
          :class="buttonsClass"
        >
          <button
            v-for="(item, index) in brandDefault.buttons"
            :key="`kakao-preview-button${index}`"
            :class="{
              'preview-message__button--kakao': item.type === 'AC',
              'preview-message__button--yellow': item.type === 'BF',
            }"
            type="button"
          >
            {{ item.name }}
          </button>
        </div>
        <div class="preview-message__coupon">
          <button
            type="button"
            :class="couponClass"
          >
            <div class="coupon__text">
              <h5 class="coupon__title">{{ couponTitle }}</h5>
              <p class="coupon__desc">{{ couponDescription }}</p>
            </div>
            <div class="coupon__icon">
              <i></i>
              <span>COUPON</span>
            </div>
          </button>
        </div>
        <PreviewFooter />
      </div>
    </div>
  </div>
</template>
<style>
</style>
